// apps/web/src/features/crm/deals/components/DealsListPage.tsx
//
// `/deals` — the v4 Deals read-view (#2248). Lists `Engagement(phase=DEAL)`
// rows via `useEngagementsList({ bucket: DEAL })` (BR-1/BR-2): the deal
// predicate is `phase=DEAL` everywhere, never "has a dealCode", so a reverted
// `paid` lead is filtered out server-side. The header carries ONE server count
// — the "Deals (won)" count from `EngagementBucketCounts.deal` — reading the
// SAME source as the list (no split-brain, BR-5). All Deal write UI + the 5-stage
// analytics were retired. A deal IS a lead at `paid`, so a row click opens its
// lead card (`/leads/:leadId`, the sole lead surface).
//
// The `?openId=` reader + `DealDetailDrawer` survive as the FROZEN request→deal
// chip's deep-link target: it resolves a LEGACY deal by id (`GET /deals/:id`,
// D-B1) and is intentionally a separate concern from the projection list.
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { EngagementBucket, type EngagementListRow } from '@magimp/shared';
import { useEngagementsList } from '@magimp/shared-hooks';
import { Button, EmptyState, ErrorState, LoadingState } from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { DealsTable } from './DealsTable.js';
import { DealDetailDrawer } from './DealDetailDrawer.js';

export function DealsListPage() {
  const { t } = useTranslation(['deals', 'common']);
  const navigate = useNavigate();
  const [openDealId, setOpenDealId] = useState<string | null>(null);
  // WHY (#1597): symmetric `?openId=` open-on-mount reader (mirrors the lead
  //   side) so the frozen request drawer's deal chip can deep-link a LEGACY
  //   deal open. The effective open id is DERIVED from URL+state.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlOpenId = searchParams.get('openId');
  const effectiveOpenId = openDealId ?? urlOpenId;
  const handleCloseDeal = () => {
    setOpenDealId(null);
    if (urlOpenId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openId');
        return next;
      });
    }
  };

  const listQ = useEngagementsList({ bucket: EngagementBucket.DEAL });
  const rows: EngagementListRow[] = (listQ.data?.pages ?? []).flatMap((p) => p.items);
  const wonCount = listQ.data?.pages[0]?.counts.deal ?? null;

  const onSelect = (row: EngagementListRow) => {
    if (row.leadId) navigate(`/leads/${row.leadId}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1, minWidth: 0 }}>
          {t('deals:title')}
        </h1>
        {wonCount !== null && (
          <span
            data-testid="deals-won-count"
            style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}
          >
            <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>{t('deals:wonCount')}</span>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{wonCount}</span>
          </span>
        )}
      </header>
      {listQ.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(listQ.error)}
          onRetry={() => void listQ.refetch()}
        />
      )}
      {listQ.isPending && <LoadingState />}
      {listQ.isSuccess && (
        <>
          <DealsTable
            rows={rows}
            onSelect={onSelect}
            emptyState={
              <EmptyState title={t('common:states.emptyTitle')} text={t('deals:empty')} />
            }
          />
          {listQ.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
              <Button
                onClick={() => void listQ.fetchNextPage()}
                disabled={listQ.isFetchingNextPage}
              >
                {listQ.isFetchingNextPage
                  ? t('common:states.loading')
                  : t('deals:actions.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
      {effectiveOpenId && <DealDetailDrawer dealId={effectiveOpenId} onClose={handleCloseDeal} />}
    </div>
  );
}
